// Filtering + grouping model for the V5 Coverage board (the Coverage twin of issue-board/
// issue-groups). Pure functions over the window's site list so the toolbar, the list body and the
// map dots share one spine.

import { SITE_STATUS_META } from './lens'
import { prettyName } from './mandal-name'

import type { GisSite, SiteStatus } from '../api/types'

export type CoverageListMode = 'sites' | 'officers' | 'surveys'

export type CoverageGroupBy = 'none' | 'status' | 'mandal' | 'officer'

export const COVERAGE_GROUP_OPTIONS: { value: CoverageGroupBy; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'status', label: 'Status' }, // the default — worst-first triage sections
  { value: 'mandal', label: 'Mandal' },
  { value: 'officer', label: 'Officer' },
]

export const coverageGroupLabelFor = (g: CoverageGroupBy): string =>
  COVERAGE_GROUP_OPTIONS.find((o) => o.value === g)?.label ?? 'None'

/** The officer bucket a site belongs to (display name; null officer → 'Unassigned'). */
export const siteOfficerKey = (s: GisSite): string => s.officerName ?? 'Unassigned'

export interface SiteFilters {
  statuses: readonly SiteStatus[]
  officers: readonly string[]
  /** Canonical mandal keys (the URL-backed page filter). */
  mandal: readonly string[]
  /** Case-insensitive match over site name / officer / mandal. Empty/absent = off. */
  query?: string
}

function siteMatchesQuery(s: GisSite, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (needle === '') {
    return true
  }
  return [s.name, s.officerName, prettyName(s.mandal)].some(
    (field) => field !== null && field.toLowerCase().includes(needle),
  )
}

/** Apply every active toolbar filter to the site list (the board + map-dot spine). */
export function filterSites(sites: GisSite[], f: SiteFilters): GisSite[] {
  return sites.filter(
    (s) =>
      (f.statuses.length === 0 || f.statuses.includes(s.status)) &&
      (f.officers.length === 0 || f.officers.includes(siteOfficerKey(s))) &&
      (f.mandal.length === 0 || f.mandal.includes(s.mandal)) &&
      siteMatchesQuery(s, f.query ?? ''),
  )
}

export interface SiteGroup {
  key: string
  label: string
  rows: GisSite[]
}

/** Canonical worst-first section order when grouping by status. */
const STATUS_ORDER: SiteStatus[] = ['overdue', 'never_surveyed', 'in_progress', 'surveyed']

/**
 * Partition `rows` (already filtered + worst-first sorted; order preserved inside each group) into
 * sections. Status uses the canonical worst-first order; mandal/officer sort by count desc.
 * Returns [] for `none` (caller renders the flat list).
 */
export function groupSites(rows: GisSite[], groupBy: CoverageGroupBy): SiteGroup[] {
  if (groupBy === 'none') {
    return []
  }
  const keyFor: (s: GisSite) => string =
    groupBy === 'status' ? (s) => s.status : groupBy === 'mandal' ? (s) => s.mandal : siteOfficerKey
  const buckets = new Map<string, GisSite[]>()
  for (const s of rows) {
    const key = keyFor(s)
    const list = buckets.get(key)
    if (list) {
      list.push(s)
    } else {
      buckets.set(key, [s])
    }
  }
  const groups = [...buckets.entries()].map(([key, list]) => ({
    key,
    label:
      groupBy === 'status'
        ? SITE_STATUS_META[key as SiteStatus].label
        : groupBy === 'mandal'
          ? prettyName(key)
          : key,
    rows: list,
  }))
  if (groupBy === 'status') {
    groups.sort(
      (a, b) => STATUS_ORDER.indexOf(a.key as SiteStatus) - STATUS_ORDER.indexOf(b.key as SiteStatus),
    )
  } else {
    groups.sort((a, b) => b.rows.length - a.rows.length || a.label.localeCompare(b.label))
  }
  return groups
}
